<template>
  <div class="dashboard-line-chart-container">
    <div class="chart-title">{{ $t('dashboard.dailyBookingSource') }}</div>
    <Skeleton v-if="lineChartLoading" width="100%" height="220px" />
    <Chart
      v-else-if="hasLineChartData"
      type="line"
      :data="lineChartData"
      :options="lineChartOptions"
      style="width: 100%; height: 300px"
    />
    <div v-else class="no-chart-data">{{ $t('dashboard.noDailyBookingStats') }}</div>
  </div>
</template>
<script setup>
import { computed, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import Chart from "primevue/chart";
import Skeleton from "primevue/skeleton";
const props = defineProps({ filter: Object });
const { t } = useI18n();
const store = useStore();
const dailyBookingSourceStats = computed(
  () => store.getters["dashboard/getDailyBookingSourceStats"] || []
);
const lineChartLoading = computed(
  () => !Array.isArray(dailyBookingSourceStats.value)
);
const hasLineChartData = computed(
  () =>
    Array.isArray(dailyBookingSourceStats.value) &&
    dailyBookingSourceStats.value.length > 0
);
const lineChartData = computed(() => {
  if (
    !Array.isArray(dailyBookingSourceStats.value) ||
    dailyBookingSourceStats.value.length === 0
  ) {
    return { labels: [], datasets: [] };
  }
  const labels = dailyBookingSourceStats.value.map(
    (item) => item.date?.split("T")[0] || ""
  );
  const appBookings = dailyBookingSourceStats.value.map(
    (item) => item.appBookings ?? 0
  );
  const companyBookings = dailyBookingSourceStats.value.map(
    (item) => item.companyBookings ?? 0
  );
  return {
    labels,
    datasets: [
      {
        label: t('dashboard.appBookings'),
        data: appBookings,
        fill: false,
        borderColor: "#6366f1",
        backgroundColor: "#6366f1",
        tension: 0.3,
        pointBackgroundColor: "#6366f1",
        pointBorderColor: "#fff",
        pointRadius: 6,
        pointHoverRadius: 8,
      },
      {
        label: t('dashboard.companyBookings'),
        data: companyBookings,
        fill: false,
        borderColor: "#10b981",
        backgroundColor: "#10b981",
        tension: 0.3,
        pointBackgroundColor: "#10b981",
        pointBorderColor: "#fff",
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };
});
const lineChartOptions = {
  plugins: {
    legend: {
      labels: {
        color: "#232946",
        font: { size: 14, weight: "bold" },
      },
      position: "top",
    },
  },
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      ticks: { color: "#232946" },
      grid: { color: "#e5e7eb" },
    },
    y: {
      ticks: { color: "#232946" },
      grid: { color: "#e5e7eb" },
      beginAtZero: true,
    },
  },
};
function fetchDailyBookingSourceStats() {
  store.dispatch("dashboard/fetchDailyBookingSourceStats", props.filter);
}
watch(() => props.filter, fetchDailyBookingSourceStats, {
  immediate: true,
  deep: true,
});
</script>
<style scoped>
.dashboard-line-chart-container {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 2px 8px 0 rgba(44, 62, 80, 0.07);
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  min-width: 0;
}
.chart-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2563eb;
  margin-bottom: 0.7rem;
  text-align: left;
  width: 100%;
  letter-spacing: 0.5px;
  padding-left: 0.2rem;
  align-self: flex-start;
}
.no-chart-data {
  width: 100%;
  text-align: center;
  color: #888;
  font-size: 1.1rem;
  padding: 2.5rem 0;
}
</style>
